import {getLogger} from "@goodgamestudios/logger";
const memoize = require("lodash.memoize");
const logger = getLogger("getDefinitionByName");
/**
 * Searches the cached modules and returns the constructor function of the module matching the param ame
 * (this will not work for dynamic imports, or for definitions that have different scopes/namespace
 * (in flash getdefinitionByName has its own sandbox and it searches in its own package)
 * @param name
 * @returns {any}
 */
export function getDefinitionByName(name: string): any {
    const cachedModules = (<any>window).cachedModlues;
    const module = cachedModules[name];
    if (module !== undefined) {
        return module;
    }
    //  core now searches assets directly in library but in game we are still using getDefinitionByName to load some assets
    const asset = getDefinitionByNameFromLibrary(name);
    if (asset !== undefined) {
        return asset;
    }
    logger.warn(`The definition "${name}" could not be found`);
    return undefined;
}

(window as any).getDefinitionByName = getDefinitionByName;

/**
 * Some classes depend on error to check it's existence, e.g. EM walkmap, we need to avoid error report for those classes
 */
const ignoredClassForError = /_walkmap/i;


/**
 * memoizedGetDefinitionByNameFromLibrary should always be used to get the objects from Library because going
 * through all the contents can take some time.
 * BUT if the item is requested before the asset is loaded, maybe the item is missing now and will be added later;
 * in that case the memoized function will return undefined, even if the item is available now.
 * As a fallback, nonMemoizedGetDefinitionByNameFromLibrary should be used to make sure the item exists.
 *
 * This is a special case and I could not reproduce this situation, but it appears in Bugsnag so it might happen
 */
const nonMemoizedGetDefinitionByNameFromLibrary = (name: string) => {
    const Library = (<any>window).Library;
    if (Library !== undefined) {
        for (let property in Library) {
            if (Library.hasOwnProperty(property)) {
                const namespace = Library[property];
                if (name in namespace) {
                    return namespace[name];
                }
            }
        }
    }
    if (!ignoredClassForError.test(name)) {
        logger.error(`The definition "${name}" could not be found in Assets Library`);
    }
    return undefined;
};

const memoizedGetDefinitionByNameFromLibrary = memoize( nonMemoizedGetDefinitionByNameFromLibrary );

/**
 * Searchs the namespaces of the Asset Bundle  and returns the constructor function of the class matching the param
 * (this will not work if a class -EVEN IF IN A DIFFERENT NAMESPACE - has the same name of another one  !!! )
 * @param name
 * @returns {any}
 */
export function getDefinitionByNameFromLibrary(name: string) {
    let out: any = memoizedGetDefinitionByNameFromLibrary(name);
    // delete bugsnag notification due to HTML-4902
    if ( !out ) {
        out = nonMemoizedGetDefinitionByNameFromLibrary(name);

        if ( out ) {
            memoizedGetDefinitionByNameFromLibrary.cache.set(name, out);
        }
    }
    return out;
}

/**
 * Maps the module name  of each exported module bundled by webpack to a its constructor function
 * @param webpackRequire
 * @returns {any}
 */
export function cacheModules(webpackRequire: any): Object {

    function isValidModuleName(name: string): boolean {
        if (/^\d+$/.test(name)) { return false; } // no numbers
        let first = name.charAt(0);
        if (first === first.toLowerCase() && first !== first.toUpperCase()) { return false; } // no lowercase
        if (first === "_") { return false; }
        return true;
    }

    const win = (<any>window);
    let cachedModules: any = win.cachedModlues;

    if (webpackRequire.precachingDone === undefined) {
        webpackRequire.precachingDone = true;

        if (!cachedModules) {
            win.cachedModlues = {};
            cachedModules = win.cachedModlues;
        }

        let duplicates: any = {};
        let array: Array<any> = webpackRequire.c;
        for (let index in array) {
            let mod = array[index], exp;
            if (mod && (exp = mod.exports)) {
                for (let prop in exp) {
                    if (prop === "a") {
                        // deal with webpack harmmony import
                        let f;
                        if (typeof (f = exp[prop]) === "function") {
                            // TODO @HTML5 HTML-2531 function name shoudn't be used
                            let fn = f.name;
                            if (cachedModules.hasOwnProperty(fn)) {
                                if (!duplicates.hasOwnProperty(fn)) { duplicates[fn] = 0; }
                                duplicates[fn]++;
                            }
                            cachedModules[fn] = f; // overwrite? ¯\_(ツ)_/¯
                        } else {
                            continue;
                        }
                    } else if (!isValidModuleName(prop) || typeof exp[prop] !== "function") {
                        continue;
                    } else {
                        if (cachedModules.hasOwnProperty(prop)) {
                            if (!duplicates.hasOwnProperty(prop)) { duplicates[prop] = 0; }
                            duplicates[prop]++;
                        }
                        cachedModules[prop] = exp[prop]; // overwrite? ¯\_(ツ)_/¯
                    }
                }
            }
        }

        logger.debug("Cached " + Object.keys(cachedModules).length + " modules. Found " + Object.keys(duplicates).length + " duplicates:");
        logger.debug(duplicates);

        // Run the precaching again with the __webpack_require__ from this scope
        createjs.precacheModules(__webpack_require__);
    }


    return cachedModules;
}
